/**
 * Module Types
 *
 * Container format for distributable Machina content packs.
 * A module packages sessions, bestiary, maps, NPCs, factions, lore,
 * and playsets into a single unit that can be validated, shared, and installed.
 *
 * Special module type: 'game-system' provides a rules engine (abilities,
 * classes, combat, magic) that other modules target.
 */
import { z } from 'zod';
import { ImportProvenanceSchema } from './system-runtime/import/provenance';
import { OutcomeLadderSchema } from './system-runtime/ladder';
import { TrackDefinitionSchema } from './system-runtime/tracks';
import { auditGameSystemRules } from './system-audit';
// =============================================================================
// MODULE IDENTITY
// =============================================================================
export const ModuleAuthorSchema = z.object({
    name: z.string(),
    url: z.string().url().optional(),
    avatar: z.string().optional(),
});
export const ModuleLicenseSchema = z.enum([
    'cc-by-4.0',
    'cc-by-sa-4.0',
    'cc-by-nc-4.0',
    'cc-by-3.0',
    'ogl-1.0a',
    'orc-1.0',
    'custom',
]);
export const ModuleTypeSchema = z.enum([
    'one-shot',
    'adventure',
    'campaign',
    'supplement',
    'world',
    'bestiary-pack',
    'map-pack',
    'game-system',
]);
// =============================================================================
// MODULE ASSETS
// =============================================================================
export const AssetTypeSchema = z.enum([
    'image',
    'token',
    'map',
    'audio',
    'video',
    'font',
    'other',
]);
export const ModuleAssetSchema = z.object({
    id: z.string(),
    type: AssetTypeSchema,
    path: z.string(), // relative path within module
    contentHash: z.string().optional(), // SHA-256 for content-addressing
    mimeType: z.string().optional(),
    sizeBytes: z.number().int().optional(),
});
// =============================================================================
// MODULE DEPENDENCIES
// =============================================================================
export const ModuleDependencySchema = z.object({
    moduleId: z.string(),
    versionRange: z.string().default('*'), // semver range
    optional: z.boolean().default(false),
});
// =============================================================================
// EXTENSION POINTS
// =============================================================================
/** Definition for a custom AI DM tool that a module provides */
export const ToolDefinitionSchema = z.object({
    name: z.string(),
    description: z.string(),
    parameters: z.record(z.string(), z.unknown()).optional(),
    handler: z.string().optional(), // path to handler function (future bundled modules)
    handlerBody: z.string().optional(), // inline JS function body (executed with ModuleAPI)
});
/** Definition for a custom /slash command */
export const CommandDefinitionSchema = z.object({
    name: z.string(), // e.g. "weather" for /weather
    description: z.string(),
    usage: z.string().optional(), // e.g. "/weather <location>"
    handler: z.string().optional(), // path to handler function (future bundled modules)
    handlerBody: z.string().optional(), // inline JS function body — receives (api, ctx), returns string
});
/** Definition for a game event handler */
export const EventHandlerDefSchema = z.object({
    event: z.string(), // e.g. "combat_start", "scene_change", "npc_death"
    handler: z.string().optional(), // path to handler function (future bundled modules)
    handlerBody: z.string().optional(), // inline JS function body — receives (api, payload), void
    priority: z.number().int().default(0), // higher = runs first
    description: z.string().optional(),
});
// =============================================================================
// GAME SYSTEM — RULES DEFINITIONS
// =============================================================================
/** A single ability score definition (system-generic) */
export const AbilityDefinitionSchema = z.object({
    id: z.string(), // "str", "dexterity", "cool", "sharp"
    name: z.string(), // "Strength", "Cool"
    abbreviation: z.string(), // "STR", "COL"
    description: z.string().optional(),
});
/** A condition/status effect definition */
export const ConditionDefinitionSchema = z.object({
    id: z.string(), // "blinded", "frightened", "marked"
    name: z.string(), // "Blinded", "Frightened"
    description: z.string(),
    hasLevels: z.boolean().default(false), // e.g. exhaustion
    maxLevel: z.number().int().optional(),
});
/** A character class definition (for class-based systems) */
export const ClassDefinitionSchema = z.object({
    id: z.string(), // "fighter", "wizard", "gunslinger"
    name: z.string(), // "Fighter", "Wizard"
    hitDie: z.number().int().optional(), // d6, d8, d10, d12
    primaryAbility: z.array(z.string()).optional(), // ability IDs
    savingThrows: z.array(z.string()).optional(),
    subclassLevel: z.number().int().optional(),
    spellcasting: z.enum(['full', 'half', 'third', 'pact', 'none']).optional(),
    description: z.string().optional(),
});
/**
 * The fields that say how ONE kind of roll reads its dice.
 *
 * Shared by `coreResolution` and each of its `variants`, so a variant can
 * override any of them and nothing else. Kept as a plain object of fields
 * rather than a schema of its own because the two places that use it differ in
 * what surrounds it: the core block adds the family label and the crit range,
 * and a variant adds the roll types it answers for.
 */
const resolutionReadingFields = {
    /**
     * How several dice become the one number the outcome ladder reads.
     *
     * Everything the runtime had seen before Trophy Dark summed its dice — 2d6
     * PbtA adds them, 4dF adds them, a d20 has only one — so summing was the
     * unstated default and nothing revealed it as a choice. Trophy reads the
     * HIGHEST die of its pool, and a runtime that summed would rate three 2s as a
     * 6: a clean success where the fiction says failure.
     *
     * `count-successes` is here for the dice-pool family it would otherwise
     * exclude (World of Darkness, Shadowrun), where the number that matters is
     * how many dice cleared a threshold rather than any single die.
     *
     * `ranked` is the highest die with ties broken by the next highest, and so on
     * down the pool. It exists for opposed pools, where two sides roll and the
     * better POOL wins: Trophy's Contest counts sixes, then fives, downward, which
     * is the same comparison. The number it produces is the pool written
     * highest-first after a decimal point — [6, 5, 5] reads 6.55, [6, 6] reads
     * 6.6 — so the whole number is still the highest die (a `natural >= 6` rung
     * means what it always meant) and two pools compare correctly as plain
     * numbers. Dice with more than nine sides take two digits each (12.0903).
     *
     * Optional rather than defaulted: a Zod `.default()` makes the field REQUIRED
     * on the inferred output type, which would force every hand-written adapter
     * that builds a `CoreResolution` literal to restate `poolReading: 'sum'`. An
     * absent value means summing, which is what every system that predates this
     * field was already doing.
     */
    poolReading: z.enum(['sum', 'highest', 'lowest', 'count-successes', 'ranked']).optional(),
    /** For `count-successes`: the value a die must reach to count as one. */
    successThreshold: z.number().int().optional(),
    /**
     * Read only the N highest dice, after `readKinds` has filtered the pool.
     *
     * The "keep highest" of ordinary dice notation (4d6kh3). Trophy's Combat Roll
     * sums the two highest dark dice, and summing all of them would let a big
     * pool beat any creature by weight of numbers. Applies before `poolReading`,
     * so `keepHighest: 2` with `sum` is "the two highest, added".
     */
    keepHighest: z.number().int().positive().optional(),
    /**
     * Which kinds of die this reading looks at, from `coreResolution.poolKinds`.
     * Absent means every die in the pool.
     *
     * The dice not named are still thrown and still shown — a Trophy player sees
     * their light dice land during a Combat Roll — they just do not feed the
     * number the ladder reads.
     */
    readKinds: z.array(z.string()).min(1).optional(),
    /**
     * The dice this system throws for a check, as an expression: "1d20", "2d6".
     *
     * `type` names a family, not dice, so the runtime has always had to infer the
     * expression from the enum — fine for the four families where the answer is
     * obvious and impossible for `custom`. A system that rolls 3d6 under a score
     * has no way to say so without this field, which is the difference between a
     * rules document that plays and one that only validates.
     *
     * A fixed expression, but only for the SHAPE of the die. A system whose pool
     * size varies per roll — Trophy hands out one light die per thing helping you
     * — states `1d6` here and passes the count on `RollContext.pool`, because the
     * size is a property of the situation rather than of the system. Systems whose
     * die SHAPE varies with the character (Savage Worlds step dice) still cannot
     * be stated here and remain code adapters.
     */
    diceExpression: z.string().optional(),
    /**
     * How this system reads its dice, when the runtime has no ready-made ladder
     * for `type`.
     *
     * Required rather than guessed for every family the runtime does not already
     * know (see `adapterFromRules`), because the alternative to an author saying
     * what a 78 on percentile dice means is the engine deciding for them, and the
     * engine's only honest default is d20's — which is the assumption this whole
     * format exists to remove. Supplying it also overrides a ready-made ladder,
     * which is how a d20 system states PF2e's four degrees.
     */
    outcomeLadder: OutcomeLadderSchema.optional(),
};
/**
 * A second way of reading the dice, for certain kinds of roll.
 *
 * `coreResolution` was one object, and the name said "core" while the schema
 * meant "only". Trophy Dark reads the same d6 pool three ways: a Risk Roll takes
 * the highest die, a Contest compares whole pools, and a Combat Roll sums the
 * two highest dark dice against a creature's Endurance. Blades splits action,
 * fortune and resistance rolls; 5e's own death save is a d20 that ignores
 * modifiers. Each of those is a variant.
 *
 * WHY THIS SHAPE, argued rather than assumed:
 *
 *  - The core block stays the default, and a variant overrides it field by
 *    field. Every existing document has no variants and reads exactly as it did.
 *    Per-field rather than whole-block replacement, so a variant that only
 *    changes the reading does not have to restate the dice, and a variant that
 *    only changes the dice keeps the ladder.
 *  - Keyed by `RollContext.type`, the same vocabulary `appliesTo` on a ladder
 *    rung already uses. The caller already says what kind of roll it is making,
 *    so choosing a reading needs no new argument anywhere in the engine.
 *  - A list with ids rather than a map from roll type, because one reading
 *    often serves several roll types, and because every other list in this
 *    format (abilities, tracks, tiers) is shaped this way. The runtime refuses
 *    a roll type claimed by two variants rather than picking one.
 *  - Only the reading fields can be overridden. `type` is the system's family
 *    label, `poolKinds` is what dice the system owns, and the crit range feeds
 *    only the ready-made d20 ladder — a variant that wants different criticals
 *    writes its own ladder, which says it more plainly.
 *
 * HOW THIS RELATES TO `appliesTo` ON A RUNG. It is the same idea at two sizes. A
 * rung's `appliesTo` is for rolls that read the dice the same way and differ in
 * one outcome (5e's natural 20 only criticals an attack). A variant is for rolls
 * that throw different dice or read them differently. Both key on the same roll
 * types, and `adapterFromRules` refuses a rung that no roll can reach because a
 * variant took all of its types away.
 */
export const ResolutionVariantSchema = z
    .object({
    id: z.string(),
    /** What the table calls this roll. "Contest Roll", "Death Save". */
    name: z.string().optional(),
    description: z.string().optional(),
    /** The `RollContext.type` values this reading answers for. */
    appliesTo: z.array(z.string()).min(1),
    ...resolutionReadingFields,
})
    .strict();
/** Core dice resolution mechanic */
export const CoreResolutionSchema = z
    .object({
    type: z.enum([
        'd20-roll-high', // D&D, PF, 13th Age
        '2d6-pbta', // Powered by the Apocalypse
        'dice-pool', // Shadowrun, World of Darkness
        'fate-dice', // Fate Core (4dF)
        'percentile', // Call of Cthulhu, WFRP
        'd100', // BRP
        'd20-roll-under', // OSR variants, Cairn
        'step-dice', // Savage Worlds
        'custom',
    ]),
    critRange: z.array(z.number().int()).optional(), // [20] for 5e, [19,20] for improved crit
    fumbleRange: z.array(z.number().int()).optional(), // [1] for 5e
    degreesOfSuccess: z.number().int().optional(), // 4 for PF2e
    ...resolutionReadingFields,
    /**
     * The kinds of die a pool can mix, in the order they are thrown. Trophy's
     * are `[light, dark]`.
     *
     * A kind is a die the fiction treats differently while the dice themselves
     * are identical: a dark die is still a d6. Declaring the kinds lets a
     * reading name the ones it counts (`readKinds`) and lets the runtime refuse
     * a reading that names a kind the system does not have. A caller builds a
     * mixed pool with `RollContext.pool: { light: 2, dark: 1 }`; a bare count
     * throws that many dice of the FIRST kind listed.
     *
     * Only the NAMES are modelled. What a kind does beyond being read or not —
     * Trophy's dark die raising Ruin when it comes up highest — depends on the
     * character's state and is not something a reading can say.
     */
    poolKinds: z.array(z.string()).min(1).optional(),
    /** Other ways of reading the dice, for named roll types. See {@link ResolutionVariantSchema}. */
    variants: z.array(ResolutionVariantSchema).optional(),
})
    /**
     * Strict for the same reason the whole document is (see the note on
     * `GameSystemRulesSchema`). It was not before, which meant a misspelled field
     * INSIDE this block — `varients:` — would have been dropped without a word,
     * and this block is where the new fields live.
     */
    .strict();
/** Action types available in combat */
export const ActionTypeSchema = z.object({
    id: z.string(), // "action", "bonus_action", "reaction", "free"
    name: z.string(),
    description: z.string().optional(),
    countPerRound: z.number().int().default(1),
});
/** Combat framework definition */
export const CombatFrameworkSchema = z.object({
    initiativeFormula: z.string().optional(), // "1d20 + dex_mod" for 5e
    actionEconomy: z.array(ActionTypeSchema).optional(),
    roundDuration: z.string().optional(), // "6 seconds" for 5e
    gridBased: z.boolean().default(true),
    gridUnit: z.string().default('5ft'), // "5ft", "1 zone", "1 square"
    deathMechanic: z.string().optional(), // "death-saves", "wound-levels", etc.
});
/** Magic / powers system definition */
export const MagicSystemSchema = z.object({
    type: z.enum([
        'spell-slots', // D&D 5e, PF2e
        'mana', // point-buy casting
        'power-points', // psionics, Cypher
        'narrative', // Fate, PbtA
        'vancian', // OSR
        'none',
        'custom',
    ]),
    castingAbilities: z.array(z.string()).optional(),
    concentrationRule: z.boolean().default(false),
    description: z.string().optional(),
});
/** Encounter balance system */
export const EncounterBalanceSchema = z.object({
    ratingSystem: z.enum(['cr', 'level', 'threat', 'none']),
    description: z.string().optional(),
});
/** Character template — what a character looks like in this system */
export const CharacterTemplateSchema = z.object({
    levelCap: z.number().int().optional(), // 20 for 5e, null for classless
    usesClasses: z.boolean().default(true),
    usesRaces: z.boolean().default(true),
    progressionType: z.enum(['xp', 'milestone', 'advancement-track', 'freeform']).default('xp'),
    xpTable: z.array(z.number().int()).optional(),
    healthModel: z.enum([
        'hit-points', // D&D, PF
        'stress-track', // Fate
        'wound-levels', // Savage Worlds, MÖRK BORG
        'fate-stress', // Fate-specific
        'custom',
    ]).default('hit-points'),
    defenseModel: z.enum([
        'armor-class', // D&D, PF
        'defense-rating', // Some OSR
        'dodge-parry', // RuneQuest
        'static', // Fate
        'custom',
    ]).default('armor-class'),
});
/** AI DM guidance — helps the AI understand the system's idioms */
export const DmGuidanceSchema = z.object({
    systemBrief: z.string(), // 2-3 paragraph overview
    combatNorms: z.string(), // What "normal" combat looks like
    socialNorms: z.string(), // How social encounters work
    toneGuidance: z.string(), // System's intended play feel
    commonMistakes: z.array(z.string()), // "Don't use spell slots — this system uses mana"
});
/** Complete rules definition for a game system */
export const GameSystemRulesSchema = z.object({
    // Core identity
    name: z.string(),
    shortName: z.string(),
    license: z.string(),
    publisher: z.string().optional(),
    /**
     * What this system calls the person running the game, table-facing.
     *
     * "Dungeon Master" is a D&D term and reads wrong everywhere else. Doomsworn
     * uses "the Narrator", which is already the language of its own design docs
     * (02-NARRATOR-CONTRACT.md) and carries the authority split: the Narrator
     * describes, and the engine remembers. Defaults to Game Master so a system
     * that does not care needs no opinion.
     */
    gamemasterTitle: z.string().optional(),
    // Ability framework
    abilities: z.array(AbilityDefinitionSchema),
    abilityModFormula: z.string().optional(), // "(score - 10) / 2" for d20 systems
    // Character structure
    characterTemplate: CharacterTemplateSchema,
    classes: z.array(ClassDefinitionSchema).optional(),
    /**
     * Named resources with a range and named bands — hit points, Ruin, stress,
     * omens, misery.
     *
     * `characterTemplate.healthModel` is the LABEL on this ("hit-points",
     * "wound-levels"); these are the contents. A system that declares no tracks
     * has a health model in name only, which is what every system had before
     * this field existed. See `system-runtime/tracks.ts`.
     */
    tracks: z.array(TrackDefinitionSchema).optional(),
    // Dice & resolution
    coreResolution: CoreResolutionSchema,
    // Combat (optional — narrative systems may skip)
    combat: CombatFrameworkSchema.optional(),
    // Conditions / status effects
    conditions: z.array(ConditionDefinitionSchema).optional(),
    // Magic / powers (optional)
    magic: MagicSystemSchema.optional(),
    // Encounter balance (optional)
    encounterBalance: EncounterBalanceSchema.optional(),
    // AI DM hints
    dmGuidance: DmGuidanceSchema.optional(),
    /**
     * Provenance, when this document was drafted from a rules text rather than
     * hand-written. Absent on hand-authored systems.
     *
     * It lives on the rules rather than on the module because it describes where
     * the RULES came from, and a module can be re-versioned, renamed and
     * republished without changing which SRD its mechanics were read out of.
     * While `import.unresolved` has entries the module does not validate — see
     * `validateGameSystemModule`.
     */
    import: ImportProvenanceSchema.optional(),
})
    /**
     * Unknown fields are an ERROR, not something to quietly drop.
     *
     * Zod strips what it does not recognise, which is the friendly default and
     * the wrong one here. This format is authored by hand, by people who are not
     * in the room, against documentation rather than a compiler — and a
     * misremembered or misspelled field name would validate, pack, publish,
     * install, and do nothing, with the author's only clue being that their
     * system does not behave as written.
     *
     * Found the hard way: `poolReading` was written into a rules document before
     * the schema had it, and the document validated clean.
     */
    .strict();
// =============================================================================
// MACHINA MODULE — THE CONTAINER
// =============================================================================
/**
 * MachinaModule is the top-level container for distributable content.
 *
 * Everything except identity fields is optional — a module can be
 * a bestiary pack, a map pack, a full campaign, or a game system.
 * Compose what you need.
 */
export const MachinaModuleSchema = z.object({
    // --- Identity (required) ---
    id: z.string(), // unique slug
    version: z.string().default('1.0.0'), // semver
    name: z.string(), // display name
    description: z.string(), // 1-3 sentences
    author: ModuleAuthorSchema,
    license: ModuleLicenseSchema.default('cc-by-4.0'),
    // --- Classification ---
    type: ModuleTypeSchema,
    tags: z.array(z.string()).default([]),
    system: z.string().default('system-agnostic'), // system module ID
    levelRange: z.tuple([z.number().int(), z.number().int()]).optional(),
    playerCount: z.tuple([z.number().int(), z.number().int()]).optional(),
    estimatedDuration: z.string().optional(),
    // --- Content (all optional — compose what you need) ---
    // These reference the existing core types by structure, not by import,
    // to avoid circular deps. Module validation checks against the active system.
    sessions: z.array(z.record(z.string(), z.unknown())).optional(),
    bestiary: z.array(z.record(z.string(), z.unknown())).optional(),
    maps: z.array(z.record(z.string(), z.unknown())).optional(),
    npcs: z.array(z.record(z.string(), z.unknown())).optional(),
    factions: z.array(z.record(z.string(), z.unknown())).optional(),
    districts: z.array(z.record(z.string(), z.unknown())).optional(),
    scenarios: z.array(z.record(z.string(), z.unknown())).optional(),
    questHooks: z.array(z.record(z.string(), z.unknown())).optional(),
    journalEntries: z.array(z.record(z.string(), z.unknown())).optional(),
    // --- Game System (only for type: 'game-system') ---
    rules: GameSystemRulesSchema.optional(),
    extends: z.string().optional(), // parent system module ID (for forks)
    // --- Extension Points ---
    customTools: z.array(ToolDefinitionSchema).optional(),
    customCommands: z.array(CommandDefinitionSchema).optional(),
    eventHandlers: z.array(EventHandlerDefSchema).optional(),
    // --- Assets ---
    assets: z.array(ModuleAssetSchema).optional(),
    thumbnail: z.string().optional(),
    // --- Dependencies ---
    requires: z.array(ModuleDependencySchema).optional(),
    compatibleWith: z.string().optional(), // minimum Machina version
});
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/** Validate that a game-system module has rules defined */
export function validateGameSystemModule(mod) {
    const errors = [];
    if (mod.type === 'game-system') {
        if (!mod.rules) {
            errors.push('Game system modules must define "rules"');
        }
        // NO ABILITY-COUNT CHECK. This used to require at least one ability, which
        // is a d20 assumption of exactly the kind the declarative runtime exists to
        // remove: Trophy Dark has no ability scores at all — not four instead of
        // six, none — and would have been rejected here as an empty stub. A system
        // is real once it has a name, a character template and a resolution
        // mechanic, all three of which the schema already requires.
        // Coherence checks live in system-audit.ts. Only the errors are taken here
        // — validateGameSystemModule has no way to report a warning, and the CLI
        // surfaces those separately.
        if (mod.rules) {
            errors.push(...auditGameSystemRules(mod.rules).errors);
        }
        // An imported draft is not a system until a human has answered what the
        // importer could not. Reported as errors rather than warnings because the
        // AI GM will run a guessed rule exactly as confidently as a real one.
        for (const question of mod.rules?.import?.unresolved ?? []) {
            errors.push(`Unresolved import question at ${question.path}: ${question.question} ` +
                `(confidence: ${question.confidence})`);
        }
    }
    if (mod.extends && mod.type !== 'game-system') {
        errors.push('"extends" is only valid for game-system modules');
    }
    return errors;
}
/** Validate basic module structure */
export function validateModule(mod) {
    const parsed = MachinaModuleSchema.safeParse(mod);
    if (!parsed.success) {
        return {
            success: false,
            errors: parsed.error.issues.map(i => `${i.path.join('.')}: ${i.message}`),
        };
    }
    const systemErrors = validateGameSystemModule(parsed.data);
    if (systemErrors.length > 0) {
        return { success: false, errors: systemErrors };
    }
    return { success: true, data: parsed.data };
}
//# sourceMappingURL=module-types.js.map